import { TCmsDocument } from '@daymade/api-contracts';
import clsx from 'clsx';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import AspectRatioContainer from 'components/about-us/AspectRatioContainer';
import EmblaCarousel from 'components/carousel2025/EmblaCarousel';
import { FadeIn } from 'components/common/FadeIn';
import { EnterButton } from 'components/home-page/enter-button/EnterButton';
import AutoScroll from 'embla-carousel-auto-scroll';
import useEmblaCarousel from 'embla-carousel-react';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { tryApi } from 'utils/api/common';
import { cmsApiClient } from 'utils/apiClient';
import { TRANSPARENT_PIXEL_GIF_DATA_URI } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';
import { resizePlasmicImage, WinnerHomepage } from 'utils/plasmicCMS';

import WinnersBottomWaveDesktop from '../../svg/Waves/winners-bottom-wave-desktop.svg';
import WinnersBottomWaveMobile from '../../svg/Waves/winners-bottom-wave-mobile.svg';
import WinnersTopWaveDesktop from '../../svg/Waves/winners-top-wave-desktop.svg';
import WinnersTopWaveMobile from '../../svg/Waves/winners-top-wave-mobile.svg';
import styles from './Winners.module.css';

interface WinnersProps {
  winners: WinnerHomepage[];
  background: 'darkBlue' | 'white';
}

const Winners = observer(({ winners, background }: WinnersProps) => {
  const userStore = useUserStore();
  const drawStore = useDrawStore();

  const [polaroids, setPolaroids] = useState<TCmsDocument[]>();

  const [hasScrolledIntoView, setHasScrolledIntoView] = useState(false);
  const carouselContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasScrolledIntoView) {
          setHasScrolledIntoView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (carouselContainerRef.current) {
      observer.observe(carouselContainerRef.current);
    }

    return () => observer.disconnect();
  }, [hasScrolledIntoView]);

  useEffect(() => {
    (async () => {
      const polaroids = await tryApi(cmsApiClient.public.listWinnerPolaroids());

      if (polaroids.status === HttpStatusCode.OK) {
        setPolaroids(polaroids.body);
      }
    })();
  }, []);

  const emblaCarouselInfo = useEmblaCarousel(
    {
      loop: true,
      inViewThreshold: 0.1,
      align: 'start',
      containScroll: 'trimSnaps',
    },
    [AutoScroll({ playOnInit: false, speed: 0.6, stopOnInteraction: false })],
  );

  const showEnterButton = !userStore.subscriptionStatusInfo.subscriberStatus;

  const winnerReviewsPairs = (winners || []).reduce<WinnerHomepage[][]>(
    (pairs, item, i) => {
      if (i % 2 === 0) {
        pairs.push([item]);
      } else {
        pairs[pairs.length - 1].push(item);
      }
      return pairs;
    },
    [],
  );

  return (
    <div>
      {background === 'darkBlue' && (
        <>
          <div
            className={clsx(
              styles.winnersTopWave,
              styles.winnersTopWaveDesktop,
            )}
          >
            <WinnersTopWaveDesktop />
          </div>
          <div
            className={clsx(styles.winnersTopWave, styles.winnersTopWaveMobile)}
          >
            <WinnersTopWaveMobile />
          </div>
        </>
      )}

      <AboutUsModule background={background}>
        <AboutUsSiteContainer styles2026>
          <div
            className={clsx(
              styles.container,
              background === 'darkBlue' && styles.backgroundDarkBlue,
            )}
          >
            <div className={styles.leftColumn}>
              <FadeIn onScroll className={styles.introHeading}>
                <p className="daymadeTypographyTaglineAccent">
                  Real people, real wins
                </p>
                <h2 className="daymadeTypographyXL">
                  Thousands of weekly winners
                </h2>
                <p
                  className={clsx(
                    'daymadeTypographySRegular',
                    styles.winnerBlurb,
                  )}
                >
                  From free entries to epic holidays: over{' '}
                  <strong>£12 million</strong> worth of prizes won
                </p>
              </FadeIn>
              {showEnterButton && (
                <FadeIn onScroll className={styles.buttonContainer}>
                  <EnterButton
                    overrideButtonText="Enter to Win"
                    overrideReferralText
                    dm2026Button
                    buttonSize="Large"
                  />
                </FadeIn>
              )}
            </div>
            <div className={styles.rightColumn}>
              <FadeIn onScroll className={styles.carouselContainer}>
                <div ref={carouselContainerRef} />
                <AspectRatioContainer useCssVariableSizes>
                  <EmblaCarousel
                    className={styles.carousel}
                    stretchItemHeight
                    emblaCarouselInfo={emblaCarouselInfo}
                    pips2026={background === 'darkBlue' ? true : 'lighter'}
                    autoScroll={hasScrolledIntoView}
                    items={winnerReviewsPairs
                      .map((pair, index) => (
                        <div key={index} className={styles.carouselPair}>
                          {pair.map((item, index) => {
                            return (
                              <div
                                key={index}
                                className={clsx(styles.carouselItem)}
                              >
                                <div className={styles.carouselImageWrapper}>
                                  <img
                                    src={
                                      resizePlasmicImage(item.image?.url) ||
                                      TRANSPARENT_PIXEL_GIF_DATA_URI
                                    }
                                    alt={`Winner image of ${item.prizeName}`}
                                    loading="lazy"
                                    className={styles.carouselImage}
                                  />
                                </div>
                                <div className={styles.prizeInfo}>
                                  <div className="daymadeTypographySBold">
                                    {item.winnerName}
                                  </div>
                                  <div
                                    className={clsx(
                                      'daymadeTypographySRegular',
                                      styles.winnerPrizeName,
                                    )}
                                  >
                                    {item.prizeName}
                                  </div>
                                </div>
                                <div className={styles.carouselItemGradient} />
                              </div>
                            );
                          })}
                        </div>
                      ))
                      .filter(Boolean)}
                  ></EmblaCarousel>
                </AspectRatioContainer>
              </FadeIn>
              {/* ENG-1884 - Polaroids no longer visible in variant V5
              <FadeIn onScroll className={styles.polaroidContainer}>
                <p className="daymadeTypographyMRegular">
                  And{' '}
                  <strong>{drawStore.uniqueWinners.toLocaleString()}+</strong>{' '}
                  more winners just like them
                </p>
                <Link href="/winners" className={styles.polaroids}>
                  {polaroids?.map((item, i) => {
                    const { data } = item;

                    if (isWinnerPolaroidType(data)) {
                      return (
                        <Polaroid
                          key={i}
                          imageSrc={data.image}
                          name={data.winnerName}
                        />
                      );
                    }

                    return null;
                  })}
                </Link>
              </FadeIn> */}
            </div>
          </div>
        </AboutUsSiteContainer>
      </AboutUsModule>

      {background === 'darkBlue' && (
        <>
          <div
            className={clsx(
              styles.winnersBottomWave,
              styles.winnersBottomWaveDesktop,
            )}
          >
            <WinnersBottomWaveDesktop />
          </div>
          <div
            className={clsx(
              styles.winnersBottomWave,
              styles.winnersBottomWaveMobile,
            )}
          >
            <WinnersBottomWaveMobile />
          </div>
        </>
      )}
    </div>
  );
});

interface PolaroidProps {
  name?: string;
  imageSrc?: string | null;
}
const Polaroid = (props: PolaroidProps) => {
  if (!props.name || !props.imageSrc) {
    return <></>;
  }
  return (
    <figure className={styles.polaroid}>
      <Image
        src={props.imageSrc || TRANSPARENT_PIXEL_GIF_DATA_URI}
        alt={`A photo of ${props.name}`}
        sizes="auto"
        width="50"
        height="50"
      />
      <figcaption className="aboutUsTypographyCaption">{props.name}</figcaption>
    </figure>
  );
};

export default Winners;
